// One reading of a service's amount settings, shared by the hint a payer sees
// and the check that refuses an amount, so the two cannot disagree.
import { MIN_PAYABLE_AMOUNT, payableAmount } from "./payableAmount";

export type AmountSettings = {
  min?: number;
  max?: number;
  denomination?: number;
};

export type AmountRules = {
  min: number;
  // null: no multiple check. A denomination of 1 or less means none.
  multiple: number | null;
  max: number | null;
  // false: the service declared nothing, so the screen keeps today's hint.
  configured: boolean;
};

export type AmountProblem = "required" | "invalid" | "below_min" | "not_multiple" | "above_max";

const positive = (value: number | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;

export const amountRules = (settings: AmountSettings | null | undefined): AmountRules => {
  const denomination = positive(settings?.denomination);

  // An amount object with every setting absent or zero declares nothing, so
  // it keeps today's behaviour like a service that sends no object at all.
  if (
    !settings ||
    (denomination === null &&
      positive(settings.min) === null &&
      positive(settings.max) === null)
  ) {
    return { min: MIN_PAYABLE_AMOUNT, multiple: null, max: null, configured: false };
  }


  return {
    min: positive(settings.min) ?? MIN_PAYABLE_AMOUNT,
    multiple: denomination !== null && denomination > 1 ? denomination : null,
    max: positive(settings.max),
    configured: true,
  };
};

export const checkAmount = (amount: string, rules: AmountRules): AmountProblem | null => {
  if (amount.trim() === "") return "required";

  const value = payableAmount(amount);

  if (!Number.isFinite(value)) return "invalid";
  if (value < rules.min) return "below_min";
  if (rules.multiple !== null && value % rules.multiple !== 0) return "not_multiple";
  if (rules.max !== null && value > rules.max) return "above_max";

  return null;
};
